import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	X,
	CircleCheck as CheckCircle2,
	Circle as XCircle,
	Package,
	ScanLine,
	TriangleAlert as AlertTriangle,
	Loader as Loader2,
	Printer,
	Store,
	ImageOff,
	Keyboard,
	Mail,
	Minus,
	Plus,
	Search,
	ArrowLeft,
	ArrowRight,
	PackagePlus,
	ClipboardList,
	Send,
} from 'lucide-react'
import { Button } from '../ui/Button'
import { HapticFeedback } from '../ui/HapticFeedback'
import { supabase } from '../../lib/supabase'
import { fetchCompanyProfile, type CompanyProfile } from '../../lib/companyProfile'
import { resolveWedgeScan } from '../../utils/resolveWedgeScan'
import { classifyScanValue } from '../../utils/scanClassify'
import type { ShopReceiptQueueItem, ShopReceiptQueueLine } from '../../hooks/useShopReceiptQueue'
import {
	buildShopReceiptReportHtml,
	printShopReceiptReport,
	summariseShopReceipt,
	type ShopReceiptReportLine,
} from './shop-receipt-report'
import {
	fetchWorkerContacts,
	isEmailConfigured,
	notifyAdminOfDiscrepancy,
	recordShopReceiptDelivery,
	sendShopReceiptReport,
	type WorkerContactOption,
} from '../../lib/shopReceiptEmail'
import { ExtraItemPicker } from './extra-item-picker'
import type { ExtraPickerProduct } from './extra-item-picker-helpers'

type Phase = 'mode' | 'verify' | 'missing' | 'extra' | 'report'
type ScanMode = 'one_by_one' | 'scan'
type ScanSource = 'manual' | 'barcode' | 'epc'

interface VerifyRow extends ShopReceiptQueueLine {
	receivedQty: number
	decided: boolean
	scanSource: ScanSource
}

interface ExtraRow {
	product_id: string
	sku: string | null
	name: string | null
	image_url: string | null
	reference_code: string | null
	color: string
	size: string
	qty: number
	scanSource: ScanSource
}

interface ProductSearchRow {
	id: string
	sku: string | null
	name: string | null
	image_url: string | null
	reference_code: string | null
	barcode: string | null
	color: string
	size: string
}

interface ShopReceiptModalProps {
	delivery: ShopReceiptQueueItem
	adminId: string | null
	userId: string | null
	userName: string | null
	userEmail: string | null
	onClose: () => void
	onComplete: () => void
}

const STEPS: { phase: Phase; label: string; short: string }[] = [
	{ phase: 'verify', label: 'Verify items', short: 'Verify' },
	{ phase: 'missing', label: 'Missing items', short: 'Missing' },
	{ phase: 'extra', label: 'Extra items', short: 'Extra' },
	{ phase: 'report', label: 'Report', short: 'Report' },
]

function variantOf(line: { color?: string | null; size?: string | null }): string {
	return [line.color, line.size].filter(Boolean).join(' / ')
}

function labelOf(line: { name?: string | null; sku?: string | null; color?: string | null; size?: string | null }): string {
	const variant = variantOf(line)
	const base = line.name || line.sku || 'Item'
	return variant ? `${base} (${variant})` : base
}

function initialsOf(value: string): string {
	return value
		.split(' ')
		.map((word) => word[0])
		.join('')
		.substring(0, 2)
		.toUpperCase()
}

export const ShopReceiptModal: React.FC<ShopReceiptModalProps> = ({
	delivery,
	adminId,
	userId,
	userName,
	userEmail,
	onClose,
	onComplete,
}) => {
	const [phase, setPhase] = useState<Phase>('mode')
	const [mode, setMode] = useState<ScanMode>('one_by_one')
	const [receiptId, setReceiptId] = useState<string | null>(null)
	const [starting, setStarting] = useState(false)
	const [actionError, setActionError] = useState<string | null>(null)

	// Only lines the warehouse actually transferred can be physically verified.
	// A line it skipped entirely has expected_qty 0 and belongs to step 2 only.
	const expectedLines = useMemo(
		() => delivery.items.filter((item) => item.expected_qty > 0),
		[delivery.items]
	)

	const [rows, setRows] = useState<VerifyRow[]>(() =>
		expectedLines.map((item) => ({ ...item, receivedQty: 0, decided: false, scanSource: 'manual' }))
	)
	const [guidedIndex, setGuidedIndex] = useState(0)
	const [imageLoading, setImageLoading] = useState(true)

	const [scanInput, setScanInput] = useState('')
	const [isManualEntry, setIsManualEntry] = useState(false)
	const [scanFeedback, setScanFeedback] = useState<{ ok: boolean; message: string } | null>(null)

	const [hasMissing, setHasMissing] = useState<boolean | null>(null)
	const [missingFlags, setMissingFlags] = useState<Set<string>>(new Set())
	const [missingSearch, setMissingSearch] = useState('')

	const [hasExtra, setHasExtra] = useState<boolean | null>(null)
	const [extras, setExtras] = useState<ExtraRow[]>([])

	const [submitting, setSubmitting] = useState(false)
	const [reportLines, setReportLines] = useState<ShopReceiptReportLine[] | null>(null)
	const [reportHtml, setReportHtml] = useState<string>('')
	const [company, setCompany] = useState<CompanyProfile | null>(null)

	const [emailPossible, setEmailPossible] = useState(true)
	const [contacts, setContacts] = useState<WorkerContactOption[]>([])
	const [recipient, setRecipient] = useState(userEmail || '')
	const [sending, setSending] = useState(false)
	const [sentTo, setSentTo] = useState<string | null>(null)
	const [emailError, setEmailError] = useState<string | null>(null)

	const inputRef = useRef<HTMLInputElement>(null)
	const scanBufferRef = useRef('')
	const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const lastScanTimeRef = useRef(0)
	const lastScanValueRef = useRef('')
	const processScanRef = useRef<((raw: string) => Promise<void>) | null>(null)
	const headingRef = useRef<HTMLHeadingElement>(null)

	const totals = useMemo(() => {
		const receivedUnits = rows.reduce((sum, r) => sum + r.receivedQty, 0)
		const expectedUnits = rows.reduce((sum, r) => sum + r.expected_qty, 0)
		const decided = rows.filter((r) => r.decided).length
		return {
			receivedUnits,
			expectedUnits,
			decided,
			pending: rows.length - decided,
			shortUnits: Math.max(expectedUnits - receivedUnits, 0),
			extraUnits: extras.reduce((sum, e) => sum + e.qty, 0),
		}
	}, [rows, extras])

	// Move focus to the new step so a keyboard or screen-reader user is not left
	// behind on the previous one.
	useEffect(() => {
		headingRef.current?.focus()
	}, [phase])

	const setRow = useCallback((itemId: string, patch: Partial<VerifyRow>) => {
		setRows((prev) => prev.map((r) => (r.item_id === itemId ? { ...r, ...patch } : r)))
	}, [])

	const advanceGuided = useCallback(() => {
		setRows((current) => {
			const nextPending = current.findIndex((r, i) => i > guidedIndex && !r.decided)
			if (nextPending !== -1) {
				setImageLoading(true)
				setGuidedIndex(nextPending)
			} else if (guidedIndex < current.length - 1) {
				setImageLoading(true)
				setGuidedIndex(guidedIndex + 1)
			}
			return current
		})
	}, [guidedIndex])

	/** Looks a barcode up against this tenant's products. */
	const findProductByCode = useCallback(
		async (code: string): Promise<ProductSearchRow | null> => {
			if (!adminId) return null
			const { data, error } = await supabase
				.from('products')
				.select('id, sku, name, image_url, reference_code, barcode, product_colors(name), product_sizes(name)')
				.eq('admin_id', adminId)
				.or(`barcode.eq.${code},sku.eq.${code},reference_code.eq.${code}`)
				.limit(1)

			if (error || !data || data.length === 0) return null
			const row = data[0] as Record<string, unknown>
			return {
				id: row.id as string,
				sku: (row.sku as string) ?? null,
				name: (row.name as string) ?? null,
				image_url: (row.image_url as string) ?? null,
				reference_code: (row.reference_code as string) ?? null,
				barcode: (row.barcode as string) ?? null,
				color: ((row.product_colors as { name?: string } | null)?.name) ?? '',
				size: ((row.product_sizes as { name?: string } | null)?.name) ?? '',
			}
		},
		[adminId]
	)

	const addExtra = useCallback(
		(product: ProductSearchRow | ExtraPickerProduct, scanSource: ScanSource, qty = 1) => {
			const addBy = Math.max(1, qty)
			setExtras((prev) => {
				const existing = prev.find((e) => e.product_id === product.id)
				if (existing) {
					return prev.map((e) =>
						e.product_id === product.id ? { ...e, qty: e.qty + addBy } : e
					)
				}
				return [
					...prev,
					{
						product_id: product.id,
						sku: product.sku,
						name: product.name,
						image_url: product.image_url,
						reference_code: product.reference_code,
						color: product.color,
						size: product.size,
						qty: addBy,
						scanSource,
					},
				]
			})
			setHasExtra(true)
		},
		[]
	)

	const addedQtyByProductId = useMemo(() => {
		const map: Record<string, number> = {}
		for (const extra of extras) map[extra.product_id] = extra.qty
		return map
	}, [extras])

	/**
	 * One scan, resolved and counted.
	 *
	 * A UHF EPC identifies a unique physical unit, so its count is exact. A
	 * barcode identifies only the SKU, so three units and one unit scanned three
	 * times look identical and the dedupe window can swallow a fast repeat. That
	 * is why the tally stays editable and why the source is recorded per line.
	 */
	const processScan = useCallback(
		async (rawValue: string) => {
			const raw = rawValue.trim()
			if (!raw) return

			const result = await resolveWedgeScan(adminId, raw)
			setScanInput('')

			if (result.kind === 'unknown' || result.kind === 'epc_error') {
				HapticFeedback.error()
				setScanFeedback({ ok: false, message: result.message })
				return
			}

			if (result.kind === 'epc' && result.integrityAlarm) {
				HapticFeedback.error()
				setScanFeedback({
					ok: false,
					message: result.message || 'Data integrity alarm - unit record and tag disagree',
				})
				return
			}

			const source: ScanSource = result.kind === 'epc' ? 'epc' : 'barcode'

			const matches = (row: VerifyRow) => {
				if (result.kind === 'epc') {
					return (
						row.product_id === result.item.id ||
						(!!result.item.sku && !!row.sku && row.sku.toLowerCase() === result.item.sku.toLowerCase())
					)
				}
				const value = result.value.trim().toLowerCase()
				return (
					(!!row.sku && row.sku.toLowerCase() === value) ||
					(!!row.barcode && row.barcode.toLowerCase() === value) ||
					(!!row.reference_code && row.reference_code.toLowerCase() === value)
				)
			}

			const target = rows.find(matches)

			if (target) {
				const next = target.receivedQty + 1
				if (next > target.expected_qty) {
					// More of this line arrived than the warehouse sent. That surplus is
					// genuinely an extra, so it goes to step 3 rather than inflating the
					// expected line past what was shipped.
					HapticFeedback.medium()
					const product: ProductSearchRow = {
						id: target.product_id,
						sku: target.sku,
						name: target.name,
						image_url: target.image_url,
						reference_code: target.reference_code,
						barcode: target.barcode,
						color: target.color,
						size: target.size,
					}
					addExtra(product, source)
					setScanFeedback({
						ok: true,
						message: `${labelOf(target)} is already complete - counted as extra`,
					})
					return
				}

				HapticFeedback.success()
				setRow(target.item_id, {
					receivedQty: next,
					decided: next >= target.expected_qty,
					scanSource: source,
				})
				setScanFeedback({
					ok: true,
					message: `${labelOf(target)} - ${next} of ${target.expected_qty}`,
				})
				return
			}

			// Nothing on this delivery matches, so it is an unexpected item.
			const product =
				result.kind === 'epc'
					? {
							id: result.item.id,
							sku: result.item.sku ?? null,
							name: result.item.name ?? null,
							image_url: result.item.image_url ?? null,
							reference_code: result.item.reference_code ?? null,
							barcode: result.item.barcode ?? null,
							color: '',
							size: '',
					  }
					: await findProductByCode(result.value)

			if (!product) {
				HapticFeedback.error()
				setScanFeedback({ ok: false, message: `"${raw}" is not on this delivery and not in inventory` })
				return
			}

			HapticFeedback.medium()
			addExtra(product, source)
			setScanFeedback({ ok: true, message: `Extra item: ${labelOf(product)}` })
		},
		[adminId, rows, setRow, addExtra, findProductByCode]
	)

	processScanRef.current = processScan

	// The listener runs for the whole of step 1 in scan mode and stops the moment
	// the worker leaves it, so a stray scan on the summary cannot change a count.
	// The input is never auto-focused: on a tablet that opens the on-screen
	// keyboard over the item being checked, and a focused field swallows the
	// wedge keystrokes the hardware scanner sends.
	useEffect(() => {
		if (phase !== 'verify' || mode !== 'scan') return

		const handleKeyPress = (e: KeyboardEvent) => {
			const target = e.target
			if (
				target instanceof HTMLInputElement ||
				target instanceof HTMLTextAreaElement ||
				target instanceof HTMLSelectElement
			) {
				return
			}

			if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current)
			const now = Date.now()

			if (e.key === 'Enter') {
				e.preventDefault()
				const buffer = scanBufferRef.current
				if (buffer.length === 0) return

				const dedupeMs = classifyScanValue(buffer) === 'epc' ? 1200 : 150
				const isDuplicate = now - lastScanTimeRef.current < dedupeMs && buffer === lastScanValueRef.current

				if (!isDuplicate) {
					lastScanTimeRef.current = now
					lastScanValueRef.current = buffer
					void processScanRef.current?.(buffer)
				}
				scanBufferRef.current = ''
				return
			}

			if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
				scanBufferRef.current += e.key
				scanTimeoutRef.current = setTimeout(() => {
					scanBufferRef.current = ''
				}, 300)
			}
		}

		window.addEventListener('keypress', handleKeyPress)
		return () => {
			window.removeEventListener('keypress', handleKeyPress)
			if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current)
		}
	}, [phase, mode])

	const handleStart = async (chosen: ScanMode) => {
		if (!adminId || starting) return
		setStarting(true)
		setActionError(null)
		try {
			const { data, error } = await supabase.rpc('start_shop_receipt', {
				p_admin_id: adminId,
				p_reservation_id: delivery.reservation_id,
				p_user_id: userId,
				p_mode: chosen,
			})
			if (error) throw error

			const res = data as { success: boolean; receipt_id?: string; error?: string }
			if (!res.success) {
				setActionError(res.error || 'Could not open this delivery')
				return
			}

			setReceiptId(res.receipt_id ?? null)
			setMode(chosen)
			setPhase('verify')
		} catch (err) {
			setActionError(err instanceof Error ? err.message : 'Could not open this delivery')
		} finally {
			setStarting(false)
		}
	}

	/**
	 * Commits the audit record, then builds the closing report from what was
	 * actually stored rather than from local state, so the worker's copy is the
	 * record and not merely his intent.
	 */
	const handleSubmit = async () => {
		if (!adminId || !receiptId || submitting) return
		setSubmitting(true)
		setActionError(null)

		try {
			const payload = [
				...rows.map((row) => ({
					kind: 'expected' as const,
					reservation_item_id: row.item_id,
					product_id: row.product_id,
					original_qty: row.original_qty,
					expected_qty: row.expected_qty,
					received_qty: row.receivedQty,
					reported_missing: missingFlags.has(row.item_id),
					scan_source: row.scanSource,
				})),
				// Lines the warehouse never sent are not verifiable in step 1, but the
				// shop can still confirm in step 2 that they never arrived. They are
				// recorded with expected_qty 0 so the arithmetic stays honest, and
				// original_qty carries what the customer was promised.
				...delivery.items
					.filter((item) => item.expected_qty === 0 && missingFlags.has(item.item_id))
					.map((item) => ({
						kind: 'expected' as const,
						reservation_item_id: item.item_id,
						product_id: item.product_id,
						original_qty: item.original_qty,
						expected_qty: 0,
						received_qty: 0,
						reported_missing: true,
						scan_source: 'manual' as const,
					})),
				...extras.map((extra) => ({
					kind: 'extra' as const,
					reservation_item_id: null,
					product_id: extra.product_id,
					original_qty: 0,
					expected_qty: 0,
					received_qty: extra.qty,
					reported_missing: false,
					scan_source: extra.scanSource,
				})),
			]

			const { data, error } = await supabase.rpc('submit_shop_receipt', {
				p_admin_id: adminId,
				p_receipt_id: receiptId,
				p_user_id: userId,
				p_lines: payload,
				p_notes: null,
			})
			if (error) throw error

			const res = data as { success: boolean; error?: string }
			if (!res.success) {
				setActionError(res.error || 'Could not save this confirmation')
				return
			}

			const [{ data: stored }, profile] = await Promise.all([
				supabase.rpc('get_shop_receipt_submissions', {
					p_admin_id: adminId,
					p_from: new Date(Date.now() - 5 * 60_000).toISOString(),
					p_to: null,
					p_limit: 50,
				}),
				fetchCompanyProfile(adminId),
			])

			const record = ((stored ?? []) as Record<string, unknown>[]).find(
				(row) => row.receipt_id === receiptId
			)
			const lines = (record?.lines as ShopReceiptReportLine[]) ?? []

			setCompany(profile)
			setReportLines(lines)

			const html = buildShopReceiptReportHtml({
				reservationNumber: delivery.reservation_number,
				customerName: delivery.customer_name,
				warehouseWorkerName: delivery.worker_name,
				confirmedByName: userName,
				mode,
				submittedAt: (record?.submitted_at as string) || new Date().toISOString(),
				lines,
				company: profile,
			})
			setReportHtml(html)
			setPhase('report')

			// Fire and forget: the record is already durable, and a mail problem must
			// not read as a failed submission.
			void notifyAdminOfDiscrepancy({
				adminId,
				reportHtml: html,
				summary: {
					reservationNumber: delivery.reservation_number,
					customerName: delivery.customer_name,
					confirmedByName: userName,
					lines,
				},
			})
		} catch (err) {
			setActionError(err instanceof Error ? err.message : 'Could not save this confirmation')
		} finally {
			setSubmitting(false)
		}
	}

	// Only load the delivery options once the report exists, so the terminal does
	// not query Brevo or the worker directory for a receipt nobody submitted.
	useEffect(() => {
		if (phase !== 'report' || !adminId) return
		let cancelled = false
		void Promise.all([isEmailConfigured(adminId), fetchWorkerContacts(adminId)]).then(
			([configured, workerContacts]) => {
				if (cancelled) return
				setEmailPossible(configured)
				setContacts(workerContacts)
			}
		)
		return () => {
			cancelled = true
		}
	}, [phase, adminId])

	const handlePrint = () => {
		if (!reportHtml) return
		printShopReceiptReport(reportHtml)
		if (adminId && receiptId) {
			void recordShopReceiptDelivery({ adminId, receiptId, printed: true })
		}
	}

	const handleSend = async () => {
		if (!adminId || !receiptId || !reportHtml || !reportLines) return
		const to = recipient.trim()
		if (!to.includes('@')) {
			setEmailError('Enter a valid email address')
			return
		}

		setSending(true)
		setEmailError(null)
		try {
			await sendShopReceiptReport({
				adminId,
				to,
				reportHtml,
				summary: {
					reservationNumber: delivery.reservation_number,
					customerName: delivery.customer_name,
					confirmedByName: userName,
					lines: reportLines,
				},
			})
			setSentTo(to)
			await recordShopReceiptDelivery({ adminId, receiptId, to })
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Could not send the report'
			setEmailError(message)
			await recordShopReceiptDelivery({ adminId, receiptId, to, error: message })
		} finally {
			setSending(false)
		}
	}

	const currentRow = rows[guidedIndex]
	const stepIndex = STEPS.findIndex((s) => s.phase === phase)
	const reportTotals = reportLines ? summariseShopReceipt(reportLines) : null

	const originalList = useMemo(() => {
		const term = missingSearch.trim().toLowerCase()
		const list = delivery.items.filter((item) => item.original_qty > 0)
		if (!term) return list
		return list.filter(
			(item) =>
				(item.sku || '').toLowerCase().includes(term) ||
				(item.name || '').toLowerCase().includes(term) ||
				(item.reference_code || '').toLowerCase().includes(term)
		)
	}, [delivery.items, missingSearch])

	const toggleMissing = (itemId: string) => {
		HapticFeedback.light()
		setMissingFlags((prev) => {
			const next = new Set(prev)
			if (next.has(itemId)) next.delete(itemId)
			else next.add(itemId)
			return next
		})
	}

	return (
		<div className="fixed inset-0 z-[60] flex items-stretch sm:items-center justify-center bg-black/50 sm:p-4">
			<div className="w-full sm:max-w-3xl lg:max-w-5xl bg-white dark:bg-gray-800 sm:rounded-2xl shadow-2xl flex flex-col h-full sm:max-h-[92vh]">
				{/* Sticky header: identity plus where the worker is in the flow. */}
				<div className="shrink-0 border-b border-gray-100 dark:border-gray-700">
					<div className="flex items-start justify-between gap-3 p-4 sm:p-5 short:py-3">
						<div className="flex items-center gap-3 min-w-0">
							<div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0">
								<Store className="h-6 w-6 text-white" />
							</div>
							<div className="min-w-0">
								<h2
									ref={headingRef}
									tabIndex={-1}
									className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate outline-none"
								>
									{delivery.reservation_number}
								</h2>
								<p className="text-sm text-gray-500 dark:text-gray-400 truncate">
									{delivery.customer_name || 'Customer'}
									{delivery.worker_name ? ` · picked by ${delivery.worker_name}` : ''}
								</p>
							</div>
						</div>
						<button
							onClick={onClose}
							aria-label="Close"
							className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
						>
							<X className="h-5 w-5" />
						</button>
					</div>

					{phase !== 'mode' && (
						<div className="flex items-center gap-1 px-4 sm:px-5 pb-3 overflow-x-auto">
							{STEPS.map((step, index) => {
								const done = index < stepIndex
								const active = index === stepIndex
								return (
									<React.Fragment key={step.phase}>
										{index > 0 && (
											<div
												className={`h-0.5 flex-1 min-w-3 rounded ${
													done ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'
												}`}
											/>
										)}
										<div
											className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
												active
													? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
													: done
													? 'text-emerald-600 dark:text-emerald-400'
													: 'text-gray-400 dark:text-gray-500'
											}`}
										>
											{done ? (
												<CheckCircle2 className="h-3.5 w-3.5" />
											) : (
												<span className="w-4 h-4 rounded-full border-2 border-current flex items-center justify-center text-[9px]">
													{index + 1}
												</span>
											)}
											<span className="hidden sm:inline">{step.label}</span>
											<span className="sm:hidden">{step.short}</span>
										</div>
									</React.Fragment>
								)
							})}
						</div>
					)}
				</div>

				{/* Only this region scrolls, so the action bar never moves under a thumb. */}
				<div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-5">
					{actionError && (
						<div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
							<AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
							<span>{actionError}</span>
						</div>
					)}

					{phase === 'mode' && (
						<div className="space-y-4 max-w-xl mx-auto py-2">
							<div className="text-center">
								<h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
									How do you want to check this delivery?
								</h3>
								<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
									{delivery.item_count} item{delivery.item_count === 1 ? '' : 's'} · {delivery.unit_count} unit
									{delivery.unit_count === 1 ? '' : 's'} sent from the warehouse
								</p>
							</div>

							<button
								onClick={() => handleStart('one_by_one')}
								disabled={starting}
								className="w-full text-left p-4 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-emerald-400 dark:hover:border-emerald-600 transition-colors disabled:opacity-60 flex items-start gap-4"
							>
								<div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
									<ClipboardList className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
								</div>
								<div>
									<p className="font-bold text-gray-900 dark:text-gray-100">One by one</p>
									<p className="text-sm text-gray-500 dark:text-gray-400">
										Each item with its photo, size and colour. Confirm received or mark missing.
									</p>
								</div>
							</button>

							<button
								onClick={() => handleStart('scan')}
								disabled={starting}
								className="w-full text-left p-4 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-cyan-400 dark:hover:border-cyan-600 transition-colors disabled:opacity-60 flex items-start gap-4"
							>
								<div className="w-12 h-12 rounded-xl bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center flex-shrink-0">
									<ScanLine className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
								</div>
								<div>
									<p className="font-bold text-gray-900 dark:text-gray-100">Scanner</p>
									<p className="text-sm text-gray-500 dark:text-gray-400">
										Keep scanning barcodes or UHF tags. Missing and extra items are detected as you go, until
										you press Finish.
									</p>
								</div>
							</button>

							{starting && (
								<p className="text-center text-sm text-gray-500 dark:text-gray-400 flex items-center justify-center gap-2">
									<Loader2 className="h-4 w-4 animate-spin" /> Opening...
								</p>
							)}
						</div>
					)}

					{phase === 'verify' && mode === 'one_by_one' && (
						<div className="space-y-4">
							{currentRow ? (
								<>
									<p className="text-center text-xs font-medium text-gray-400 dark:text-gray-500">
										Item {guidedIndex + 1} of {rows.length}
									</p>

									<div
										className={`rounded-2xl border-2 p-4 sm:p-5 transition-colors ${
											!currentRow.decided
												? 'border-gray-200 dark:border-gray-700'
												: currentRow.receivedQty >= currentRow.expected_qty
												? 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/60 dark:bg-emerald-900/20'
												: 'border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/20'
										}`}
									>
										<div className="flex flex-col sm:flex-row items-center gap-5">
											<div className="relative flex-shrink-0">
												{currentRow.image_url ? (
													<>
														{imageLoading && (
															<div className="absolute inset-0 w-36 h-36 bg-gray-100 dark:bg-gray-700 rounded-2xl animate-pulse flex items-center justify-center">
																<Loader2 className="h-7 w-7 text-gray-400 animate-spin" />
															</div>
														)}
														<img
															src={currentRow.image_url}
															alt={labelOf(currentRow)}
															crossOrigin="anonymous"
															onLoad={() => setImageLoading(false)}
															onError={() => setImageLoading(false)}
															className={`w-36 h-36 object-contain rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 transition-opacity ${
																imageLoading ? 'opacity-0' : 'opacity-100'
															}`}
														/>
													</>
												) : (
													<div className="w-36 h-36 rounded-2xl flex flex-col items-center justify-center border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700">
														<ImageOff className="h-9 w-9 text-gray-400 mb-1" />
														<span className="text-2xl font-bold text-gray-400">
															{initialsOf(currentRow.name || currentRow.sku || 'Item')}
														</span>
													</div>
												)}
											</div>

											<div className="flex-1 min-w-0 text-center sm:text-left">
												<h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
													{currentRow.name || currentRow.sku || 'Item'}
												</h3>
												{currentRow.sku && (
													<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
														SKU:{' '}
														<span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
															{currentRow.sku}
														</span>
													</p>
												)}
												{currentRow.reference_code && (
													<p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
														Ref {currentRow.reference_code}
													</p>
												)}

												{(currentRow.color || currentRow.size) && (
													<div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-3">
														{currentRow.color && (
															<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-pink-50 dark:bg-pink-900/30 border border-pink-200 dark:border-pink-800 text-pink-700 dark:text-pink-300 text-sm">
																<span className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">
																	Colour
																</span>
																<span className="font-bold">{currentRow.color}</span>
															</span>
														)}
														{currentRow.size && (
															<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-sm">
																<span className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">
																	Size
																</span>
																<span className="font-bold">{currentRow.size}</span>
															</span>
														)}
													</div>
												)}

												<div className="flex items-center justify-center sm:justify-start gap-3 mt-4">
													<div className="flex items-baseline gap-1.5 px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800">
														<span className="text-3xl font-black text-emerald-600 dark:text-emerald-400">
															{currentRow.expected_qty}
														</span>
														<span className="text-sm text-emerald-700 dark:text-emerald-300 font-medium">
															sent
														</span>
													</div>
													{currentRow.original_qty > currentRow.expected_qty && (
														<span className="px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-sm font-medium">
															{currentRow.original_qty} reserved
														</span>
													)}
												</div>
											</div>
										</div>

										{/* Partial counts are the normal case, not an edge case, so the
										    stepper is always available rather than hidden behind a mode. */}
										{currentRow.expected_qty > 1 && (
											<div className="mt-4 flex items-center justify-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
												<span className="text-sm text-gray-500 dark:text-gray-400">Received</span>
												<div className="flex items-center gap-3">
													<button
														onClick={() =>
															setRow(currentRow.item_id, {
																receivedQty: Math.max(currentRow.receivedQty - 1, 0),
															})
														}
														disabled={currentRow.receivedQty <= 0}
														aria-label="One fewer"
														className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center disabled:opacity-40"
													>
														<Minus className="h-5 w-5" />
													</button>
													<span className="text-2xl font-black text-gray-900 dark:text-gray-100 w-12 text-center">
														{currentRow.receivedQty}
													</span>
													<button
														onClick={() =>
															setRow(currentRow.item_id, {
																receivedQty: Math.min(currentRow.receivedQty + 1, currentRow.expected_qty),
															})
														}
														disabled={currentRow.receivedQty >= currentRow.expected_qty}
														aria-label="One more"
														className="w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center justify-center disabled:opacity-40"
													>
														<Plus className="h-5 w-5" />
													</button>
												</div>
												<span className="text-sm text-gray-400">of {currentRow.expected_qty}</span>
											</div>
										)}
									</div>

									<div className="flex gap-3">
										<Button
											variant="secondary"
											onClick={() => {
												HapticFeedback.medium()
												setRow(currentRow.item_id, { receivedQty: 0, decided: true, scanSource: 'manual' })
												advanceGuided()
											}}
											className="flex-1 py-3.5 text-base text-amber-700 dark:text-amber-300"
										>
											<XCircle className="h-5 w-5 mr-2" /> Missing
										</Button>
										<Button
											onClick={() => {
												HapticFeedback.success()
												setRow(currentRow.item_id, {
													receivedQty:
														currentRow.receivedQty > 0 ? currentRow.receivedQty : currentRow.expected_qty,
													decided: true,
													scanSource: 'manual',
												})
												advanceGuided()
											}}
											className="flex-1 py-3.5 text-base bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
										>
											<CheckCircle2 className="h-5 w-5 mr-2" /> Confirm received
										</Button>
									</div>

									<div className="flex flex-wrap gap-1.5 justify-center pt-1">
										{rows.map((row, idx) => (
											<button
												key={row.item_id}
												onClick={() => {
													setImageLoading(true)
													setGuidedIndex(idx)
												}}
												title={labelOf(row)}
												className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
													idx === guidedIndex
														? 'ring-2 ring-emerald-400 ' +
														  (!row.decided
																? 'bg-emerald-500 text-white'
																: row.receivedQty >= row.expected_qty
																? 'bg-emerald-600 text-white'
																: 'bg-amber-500 text-white')
														: !row.decided
														? 'bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
														: row.receivedQty >= row.expected_qty
														? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
														: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
												}`}
											>
												{!row.decided ? (
													idx + 1
												) : row.receivedQty >= row.expected_qty ? (
													<CheckCircle2 className="h-4 w-4" />
												) : (
													<XCircle className="h-4 w-4" />
												)}
											</button>
										))}
									</div>
								</>
							) : (
								<p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
									The warehouse did not send any items for this reservation.
								</p>
							)}
						</div>
					)}

					{phase === 'verify' && mode === 'scan' && (
						<div className="space-y-3">
							{/* Feedback sits inline under the header, never as a dialog, so the
							    list and the tally stay visible while the worker keeps scanning. */}
							<div
								aria-live="polite"
								className={`rounded-xl px-4 py-3 text-sm font-medium min-h-[3rem] flex items-center gap-2 ${
									!scanFeedback
										? 'bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-300'
										: scanFeedback.ok
										? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
										: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
								}`}
							>
								{!scanFeedback ? (
									<>
										<ScanLine className="h-4 w-4 flex-shrink-0 animate-pulse" />
										Scanner ready - keep scanning, then press Finish scanning
									</>
								) : (
									<>
										{scanFeedback.ok ? (
											<CheckCircle2 className="h-4 w-4 flex-shrink-0" />
										) : (
											<AlertTriangle className="h-4 w-4 flex-shrink-0" />
										)}
										{scanFeedback.message}
									</>
								)}
							</div>

							<div className="relative">
								<ScanLine className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
								<input
									ref={inputRef}
									type="text"
									value={scanInput}
									onChange={(e) => setScanInput(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											e.preventDefault()
											void processScan(scanInput)
										}
									}}
									placeholder={isManualEntry ? 'Type SKU or reference...' : 'Scan barcode or UHF tag...'}
									inputMode={isManualEntry ? 'text' : 'none'}
									autoComplete="off"
									autoCorrect="off"
									spellCheck={false}
									className="w-full pl-12 pr-14 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
								/>
								<button
									type="button"
									onClick={() => {
										const next = !isManualEntry
										setIsManualEntry(next)
										if (next) inputRef.current?.focus()
										else inputRef.current?.blur()
									}}
									title={isManualEntry ? 'Manual entry on - tap to return to scanner only' : 'Type manually'}
									className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors ${
										isManualEntry
											? 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400'
											: 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
									}`}
								>
									<Keyboard className="h-5 w-5" />
								</button>
							</div>

							<div className="space-y-2">
								{rows.map((row) => {
									const complete = row.receivedQty >= row.expected_qty
									return (
										<div
											key={row.item_id}
											className={`p-3 rounded-xl border flex items-center gap-3 transition-colors ${
												row.receivedQty === 0
													? 'border-gray-200 dark:border-gray-700'
													: complete
													? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20'
													: 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
											}`}
										>
											<div className="w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex items-center justify-center">
												{row.image_url ? (
													<img
														src={row.image_url}
														alt={labelOf(row)}
														crossOrigin="anonymous"
														className="w-full h-full object-contain"
													/>
												) : (
													<ImageOff className="h-5 w-5 text-gray-400" />
												)}
											</div>
											<div className="flex-1 min-w-0">
												<p className="font-medium text-gray-900 dark:text-gray-100 truncate">{labelOf(row)}</p>
												<p className="text-xs text-gray-500 dark:text-gray-400">
													{row.sku ? `SKU ${row.sku}` : ''}
													{row.scanSource !== 'manual' && (
														<span className="text-cyan-600 dark:text-cyan-400">
															{' '}
															· {row.scanSource === 'epc' ? 'UHF tag' : 'barcode'}
														</span>
													)}
												</p>
											</div>
											{/* A barcode only proves the SKU was seen, not how many units
											    there are, so the count stays adjustable by hand. */}
											<div className="flex items-center gap-2 flex-shrink-0">
												<button
													onClick={() => setRow(row.item_id, { receivedQty: Math.max(row.receivedQty - 1, 0) })}
													disabled={row.receivedQty <= 0}
													aria-label={`One fewer ${labelOf(row)}`}
													className="w-9 h-9 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 flex items-center justify-center disabled:opacity-40"
												>
													<Minus className="h-4 w-4" />
												</button>
												<span
													className={`text-sm font-bold w-12 text-center ${
														complete
															? 'text-emerald-600 dark:text-emerald-400'
															: 'text-gray-600 dark:text-gray-300'
													}`}
												>
													{row.receivedQty} / {row.expected_qty}
												</span>
												<button
													onClick={() =>
														setRow(row.item_id, {
															receivedQty: Math.min(row.receivedQty + 1, row.expected_qty),
														})
													}
													disabled={row.receivedQty >= row.expected_qty}
													aria-label={`One more ${labelOf(row)}`}
													className="w-9 h-9 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 flex items-center justify-center disabled:opacity-40"
												>
													<Plus className="h-4 w-4" />
												</button>
											</div>
										</div>
									)
								})}
							</div>

							{extras.length > 0 && (
								<div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 p-3">
									<p className="text-sm font-semibold text-violet-700 dark:text-violet-300 flex items-center gap-2 mb-2">
										<PackagePlus className="h-4 w-4" />
										{extras.length} extra item{extras.length === 1 ? '' : 's'} detected
									</p>
									<ul className="text-sm text-violet-700 dark:text-violet-300 space-y-0.5">
										{extras.map((extra) => (
											<li key={extra.product_id}>
												{extra.qty} &times; {labelOf(extra)}
											</li>
										))}
									</ul>
								</div>
							)}
						</div>
					)}

					{phase === 'missing' && (
						<div className="space-y-4">
							<div className="text-center">
								<h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
									Is anything missing from this reservation?
								</h3>
								<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
									Search the items that were originally reserved and mark whatever never reached the shop.
								</p>
							</div>

							{hasMissing === null ? (
								<div className="flex gap-3 max-w-md mx-auto pt-2">
									<Button
										variant="secondary"
										onClick={() => {
											setHasMissing(false)
											setMissingFlags(new Set())
										}}
										className="flex-1 py-3.5 text-base"
									>
										No, nothing missing
									</Button>
									<Button
										onClick={() => setHasMissing(true)}
										className="flex-1 py-3.5 text-base bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
									>
										Yes
									</Button>
								</div>
							) : hasMissing === false ? (
								<div className="text-center py-6">
									<CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
									<p className="text-sm text-gray-500 dark:text-gray-400">
										Nothing reported missing beyond the counts in step 1.
									</p>
									<button
										onClick={() => setHasMissing(null)}
										className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline mt-2"
									>
										Change this
									</button>
								</div>
							) : (
								<>
									<div className="relative">
										<Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
										<input
											type="text"
											value={missingSearch}
											onChange={(e) => setMissingSearch(e.target.value)}
											placeholder="Search the reserved items..."
											className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
										/>
									</div>

									<div className="space-y-2">
										{originalList.map((item) => {
											const flagged = missingFlags.has(item.item_id)
											const notSent = item.expected_qty === 0
											return (
												<button
													key={item.item_id}
													onClick={() => toggleMissing(item.item_id)}
													className={`w-full p-3 rounded-xl border flex items-center gap-3 text-left transition-colors ${
														flagged
															? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20'
															: 'border-gray-200 dark:border-gray-700 hover:border-amber-200 dark:hover:border-amber-800'
													}`}
												>
													<div className="w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex items-center justify-center">
														{item.image_url ? (
															<img
																src={item.image_url}
																alt={labelOf(item)}
																crossOrigin="anonymous"
																className="w-full h-full object-contain"
															/>
														) : (
															<ImageOff className="h-5 w-5 text-gray-400" />
														)}
													</div>
													<div className="flex-1 min-w-0">
														<p className="font-medium text-gray-900 dark:text-gray-100 truncate">
															{labelOf(item)}
														</p>
														<p className="text-xs text-gray-500 dark:text-gray-400">
															{item.sku ? `SKU ${item.sku} · ` : ''}
															{item.original_qty} reserved
															{notSent && (
																<span className="text-amber-600 dark:text-amber-400">
																	{' '}
																	· warehouse sent none
																</span>
															)}
														</p>
													</div>
													<div
														className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
															flagged
																? 'bg-amber-500 border-amber-500 text-white'
																: 'border-gray-300 dark:border-gray-600'
														}`}
													>
														{flagged && <CheckCircle2 className="h-4 w-4" />}
													</div>
												</button>
											)
										})}
										{originalList.length === 0 && (
											<p className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
												No reserved items match "{missingSearch}".
											</p>
										)}
									</div>
								</>
							)}
						</div>
					)}

					{phase === 'extra' && (
						<div className="space-y-4">
							<div className="text-center">
								<h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
									Did you receive anything extra?
								</h3>
								<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
									Items that arrived but were not on this reservation.
								</p>
							</div>

							{hasExtra === null ? (
								<div className="flex gap-3 max-w-md mx-auto pt-2">
									<Button
										variant="secondary"
										onClick={() => {
											setHasExtra(false)
											setExtras([])
										}}
										className="flex-1 py-3.5 text-base"
									>
										No extras
									</Button>
									<Button
										onClick={() => setHasExtra(true)}
										className="flex-1 py-3.5 text-base bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
									>
										Yes
									</Button>
								</div>
							) : hasExtra === false ? (
								<div className="text-center py-6">
									<CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
									<p className="text-sm text-gray-500 dark:text-gray-400">No extra items reported.</p>
									<button
										onClick={() => setHasExtra(null)}
										className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline mt-2"
									>
										Change this
									</button>
								</div>
							) : (
								<>
									{extras.length > 0 && (
										<div className="space-y-2">
											{extras.map((extra) => (
												<div
													key={extra.product_id}
													className="p-3 rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 flex items-center gap-3"
												>
													<div className="w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden border border-violet-200 dark:border-violet-800 bg-white dark:bg-gray-900 flex items-center justify-center">
														{extra.image_url ? (
															<img
																src={extra.image_url}
																alt={labelOf(extra)}
																crossOrigin="anonymous"
																className="w-full h-full object-contain"
															/>
														) : (
															<ImageOff className="h-5 w-5 text-gray-400" />
														)}
													</div>
													<div className="flex-1 min-w-0">
														<p className="font-medium text-gray-900 dark:text-gray-100 truncate">
															{labelOf(extra)}
														</p>
														{extra.sku && (
															<p className="text-xs text-gray-500 dark:text-gray-400">SKU {extra.sku}</p>
														)}
													</div>
													<div className="flex items-center gap-2 flex-shrink-0">
														<button
															onClick={() =>
																setExtras((prev) =>
																	prev
																		.map((e) =>
																			e.product_id === extra.product_id ? { ...e, qty: e.qty - 1 } : e
																		)
																		.filter((e) => e.qty > 0)
																)
															}
															aria-label={`One fewer ${labelOf(extra)}`}
															className="w-9 h-9 rounded-lg bg-white dark:bg-gray-800 border border-violet-200 dark:border-violet-700 text-violet-600 dark:text-violet-300 flex items-center justify-center"
														>
															<Minus className="h-4 w-4" />
														</button>
														<span className="text-sm font-bold w-6 text-center text-violet-700 dark:text-violet-300">
															{extra.qty}
														</span>
														<button
															onClick={() =>
																setExtras((prev) =>
																	prev.map((e) =>
																		e.product_id === extra.product_id ? { ...e, qty: e.qty + 1 } : e
																	)
																)
															}
															aria-label={`One more ${labelOf(extra)}`}
															className="w-9 h-9 rounded-lg bg-white dark:bg-gray-800 border border-violet-200 dark:border-violet-700 text-violet-600 dark:text-violet-300 flex items-center justify-center"
														>
															<Plus className="h-4 w-4" />
														</button>
													</div>
												</div>
											))}
										</div>
									)}

									<ExtraItemPicker
										adminId={adminId}
										addedQtyByProductId={addedQtyByProductId}
										onAdd={(product, qty) => addExtra(product, 'manual', qty)}
									/>
								</>
							)}
						</div>
					)}

					{phase === 'report' && reportTotals && (
						<div className="space-y-5">
							<div className="text-center">
								<div className="w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
									<CheckCircle2 className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
								</div>
								<h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Confirmation saved</h3>
								<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
									{delivery.reservation_number} is recorded. No stock was changed.
								</p>
							</div>

							<div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
								<div className="rounded-2xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4">
									<p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-400">
										Received
									</p>
									<p className="text-3xl font-black text-emerald-700 dark:text-emerald-300">
										{reportTotals.receivedUnits}
									</p>
									<p className="text-xs text-emerald-600/70 dark:text-emerald-400/70">
										{reportTotals.receivedLines.length} line
										{reportTotals.receivedLines.length === 1 ? '' : 's'}
									</p>
								</div>
								<div className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
									<p className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-400">
										Missing
									</p>
									<p className="text-3xl font-black text-amber-700 dark:text-amber-300">
										{reportTotals.missingUnits}
									</p>
									<p className="text-xs text-amber-600/70 dark:text-amber-400/70">
										{reportTotals.missingLines.length} line
										{reportTotals.missingLines.length === 1 ? '' : 's'}
									</p>
								</div>
								<div className="rounded-2xl border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 p-4">
									<p className="text-[10px] uppercase tracking-wider font-bold text-violet-700 dark:text-violet-400">
										Extra
									</p>
									<p className="text-3xl font-black text-violet-700 dark:text-violet-300">
										{reportTotals.extraUnits}
									</p>
									<p className="text-xs text-violet-600/70 dark:text-violet-400/70">
										{reportTotals.extraLines.length} line
										{reportTotals.extraLines.length === 1 ? '' : 's'}
									</p>
								</div>
							</div>

							{[
								{ title: 'Missing', lines: reportTotals.missingLines, tone: 'amber' as const },
								{ title: 'Extra', lines: reportTotals.extraLines, tone: 'violet' as const },
							]
								.filter((group) => group.lines.length > 0)
								.map((group) => (
									<div
										key={group.title}
										className={`rounded-xl border overflow-hidden ${
											group.tone === 'amber'
												? 'border-amber-200 dark:border-amber-800'
												: 'border-violet-200 dark:border-violet-800'
										}`}
									>
										<div
											className={`px-4 py-2 text-xs font-bold uppercase tracking-wider ${
												group.tone === 'amber'
													? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
													: 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300'
											}`}
										>
											{group.title}
										</div>
										<div className="divide-y divide-gray-100 dark:divide-gray-700">
											{group.lines.map((line, index) => (
												<div key={`${line.sku}-${index}`} className="px-4 py-2.5 text-sm">
													<p className="font-medium text-gray-900 dark:text-gray-100">
														{line.product_name || line.sku || 'Item'}
														{variantOf(line) && (
															<span className="text-gray-500 dark:text-gray-400"> ({variantOf(line)})</span>
														)}
													</p>
													<p className="text-xs text-gray-500 dark:text-gray-400">
														{line.sku ? `SKU ${line.sku} · ` : ''}
														{group.title === 'Missing'
															? `${
																	line.missing_qty > 0
																		? line.missing_qty
																		: Math.max(line.original_qty - line.received_qty, 0)
															  } missing of ${line.expected_qty > 0 ? line.expected_qty : line.original_qty}`
															: `${line.received_qty} received`}
													</p>
												</div>
											))}
										</div>
									</div>
								))}

							{/* Delivery of the report. The submission is already durable, so
							    anything that fails here is reported on its own terms. */}
							<div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
								<p className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
									<Mail className="h-4 w-4" /> Take a copy
								</p>

								{sentTo ? (
									<div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-sm text-emerald-700 dark:text-emerald-300 flex items-start gap-2">
										<CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
										<span>
											Report sent to <strong>{sentTo}</strong>. Delivery can take a minute.
										</span>
									</div>
								) : !emailPossible ? (
									<div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-700 dark:text-amber-300 flex items-start gap-2">
										<AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
										<span>
											Email is not set up for this company yet, so use Print. An admin can enable it under
											Settings, Brevo Email.
										</span>
									</div>
								) : (
									<>
										<div className="flex flex-col sm:flex-row gap-2">
											<input
												type="email"
												value={recipient}
												onChange={(e) => setRecipient(e.target.value)}
												placeholder="name@company.com"
												className="flex-1 px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
											/>
											<Button
												onClick={handleSend}
												disabled={sending || !recipient.trim()}
												className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 py-3 sm:px-6"
											>
												{sending ? (
													<Loader2 className="h-4 w-4 mr-2 animate-spin" />
												) : (
													<Send className="h-4 w-4 mr-2" />
												)}
												Send
											</Button>
										</div>

										<div className="flex flex-wrap gap-2">
											{userEmail && (
												<button
													onClick={() => setRecipient(userEmail)}
													className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
														recipient === userEmail
															? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
															: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
													}`}
												>
													My email
												</button>
											)}
											{contacts
												.filter((contact) => contact.email !== userEmail)
												.map((contact) => (
													<button
														key={contact.id}
														onClick={() => setRecipient(contact.email)}
														title={contact.email}
														className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
															recipient === contact.email
																? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
																: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
														}`}
													>
														{contact.name}
													</button>
												))}
										</div>

										{emailError && (
											<p className="text-sm text-red-600 dark:text-red-400 flex items-start gap-2">
												<AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
												<span>
													{emailError} The confirmation is saved either way - you can print it instead.
												</span>
											</p>
										)}
									</>
								)}

								<Button variant="secondary" onClick={handlePrint} className="w-full py-3">
									<Printer className="h-4 w-4 mr-2" /> Print report
								</Button>
							</div>
						</div>
					)}
				</div>

				{/* Sticky action bar. Never scrolls away, never hides behind a keyboard. */}
				{phase !== 'mode' && (
					<div className="shrink-0 border-t border-gray-100 dark:border-gray-700 p-4 pb-keyboard bg-white dark:bg-gray-800 sm:rounded-b-2xl">
						{phase === 'verify' && (
							<div className="flex items-center justify-between gap-3">
								<div className="text-sm text-gray-500 dark:text-gray-400 min-w-0">
									<span className="font-semibold text-gray-900 dark:text-gray-100">
										{totals.receivedUnits}
									</span>{' '}
									of {totals.expectedUnits} units
									{totals.extraUnits > 0 && (
										<span className="text-violet-600 dark:text-violet-400"> · {totals.extraUnits} extra</span>
									)}
									{mode === 'one_by_one' && totals.pending > 0 && (
										<span className="block text-xs text-amber-600 dark:text-amber-400">
											{totals.pending} item{totals.pending === 1 ? '' : 's'} still to check
										</span>
									)}
								</div>
								<Button
									onClick={() => setPhase('missing')}
									disabled={mode === 'one_by_one' && totals.pending > 0}
									className="flex-shrink-0 py-3 px-5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
								>
									{mode === 'scan' ? 'Finish scanning' : 'Next'}
									<ArrowRight className="h-4 w-4 ml-2" />
								</Button>
							</div>
						)}

						{(phase === 'missing' || phase === 'extra') && (
							<div className="flex items-center justify-between gap-3">
								<Button
									variant="secondary"
									onClick={() => setPhase(phase === 'missing' ? 'verify' : 'missing')}
									className="py-3"
								>
									<ArrowLeft className="h-4 w-4 mr-2" /> Back
								</Button>
								{phase === 'missing' ? (
									<Button
										onClick={() => setPhase('extra')}
										disabled={hasMissing === null}
										className="py-3 px-5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
									>
										Next <ArrowRight className="h-4 w-4 ml-2" />
									</Button>
								) : (
									<Button
										onClick={handleSubmit}
										disabled={hasExtra === null || submitting}
										className="py-3 px-5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
									>
										{submitting ? (
											<Loader2 className="h-4 w-4 mr-2 animate-spin" />
										) : (
											<CheckCircle2 className="h-4 w-4 mr-2" />
										)}
										Submit confirmation
									</Button>
								)}
							</div>
						)}

						{phase === 'report' && (
							<div className="flex items-center justify-end">
								<Button
									onClick={onComplete}
									className="py-3 px-6 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
								>
									<Package className="h-4 w-4 mr-2" /> Done
								</Button>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	)
}
